import { useEffect, useMemo, useState } from 'react';

import { PathName, TraffickingTableName } from '../../../../constants';
import { useFiltersContext } from '../../../../contexts/FilterContext';
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags';
import type { AdModel, LineItemModel } from '../../TraffickingPage/modelConverters';
import type { ReviewTableName } from '../types';

interface ReturnType {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  reviewPath: string;
  disabledButton: boolean;
  useCampaignReviewer: boolean;
}

const buildCampaignReviewerPath = (campaignIds: string[], lineItemIds: string[], adIds: string[]): string => {
  const params = new URLSearchParams();

  if (campaignIds.length) params.set('campaignIds', campaignIds.join(','));
  if (lineItemIds.length) params.set('lineItemIds', lineItemIds.join(','));
  if (adIds.length) params.set('adIds', adIds.join(','));

  const query = params.toString();
  return `${PathName.campaignReviewer}/submit${query ? `?${query}` : ''}`;
};

interface UseCreateReviewButtonOptions {
  tableName: ReviewTableName;
  selectedAdRows?: AdModel[];
  selectedLineItemRows?: LineItemModel[];
}

export const useCreateReviewButton = ({
  tableName,
  selectedAdRows,
  selectedLineItemRows,
}: UseCreateReviewButtonOptions): ReturnType => {
  const [showModal, setShowModal] = useState(false);
  const [idsList, setIdsList] = useState(['']);
  const [reviewPath, setReviewPath] = useState('');
  const [disabledButton, setDisabledButton] = useState(false);

  const { adsVsmCampaignReviewUi } = useFeatureFlags();
  const { filters } = useFiltersContext();

  const campaignIdsFromRows = useMemo(() => {
    const fromLineItems = (selectedLineItemRows || []).map((li) => li.campaign?.id).filter((id): id is string => !!id);
    const fromAds = (selectedAdRows || []).map((ad) => ad.lineItem?.campaign?.id).filter((id): id is string => !!id);
    return [...new Set([...fromLineItems, ...fromAds])];
  }, [selectedLineItemRows, selectedAdRows]);

  useEffect(() => {
    const selectedRows = filters[tableName]?.selectedRowIds || [];
    setIdsList(selectedRows);
  }, [filters, tableName]);

  useEffect(() => {
    if (adsVsmCampaignReviewUi) {
      const campaignIdsFromFilter = filters[TraffickingTableName.campaigns]?.selectedRowIds || [];
      const campaignIds = [...new Set([...campaignIdsFromFilter, ...campaignIdsFromRows])];
      const lineItemIds = filters[TraffickingTableName.lineItems]?.selectedRowIds || [];
      const adIds = filters[TraffickingTableName.ads]?.selectedRowIds || [];
      const allSelected = [...campaignIds, ...lineItemIds, ...adIds];
      setDisabledButton(allSelected.length < 1);
      setReviewPath(buildCampaignReviewerPath(campaignIds, lineItemIds, adIds));
    } else {
      setDisabledButton(idsList.length < 1);
      setReviewPath(`${PathName.campaignReview}?${tableName}Ids=${idsList}`);
    }
  }, [idsList, tableName, adsVsmCampaignReviewUi, filters, campaignIdsFromRows]);

  return {
    showModal,
    disabledButton,
    setShowModal,
    reviewPath,
    useCampaignReviewer: !!adsVsmCampaignReviewUi,
  };
};

export default useCreateReviewButton;
